import React, { useEffect, useState } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, ArrowUpDown, X, ChevronDown } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import EmptyState from './EmptyState';
import { Button } from './button';

const toCssSize = (value) => {
  if (value === undefined || value === null) return undefined;
  return typeof value === 'number' ? `${value}px` : value;
};

const getColumnStyle = (col) => ({
  width: toCssSize(col.width),
  minWidth: toCssSize(col.minWidth || col.width),
  maxWidth: toCssSize(col.maxWidth),
  verticalAlign: 'top'
});

const AdvancedSelectHeader = ({ selectable, pagination, data }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('number'); // number, page, all
  const [peopleCount, setPeopleCount] = useState(25);
  const [maxPerCompanyCheck, setMaxPerCompanyCheck] = useState(false);
  const [maxPerCompany, setMaxPerCompany] = useState(1);

  const totalPageIds = data.map(row => selectable.rowId ? selectable.rowId(row) : (row.assignmentId || row._id));
  const isChecked = selectable.selectedIds.length > 0;
  const totalRecords = pagination?.totalRecords || 0;

  const handleApply = () => {
     setIsOpen(false);
     if (selectable.onAdvancedSelect) {
         selectable.onAdvancedSelect({ mode, peopleCount, maxPerCompany: maxPerCompanyCheck ? maxPerCompany : null });
     } else {
         // Fallback if no specific handler
         if (mode === 'page') {
             selectable.onSelectionChange(Array.from(new Set([...selectable.selectedIds, ...totalPageIds])));
         } else if (mode === 'all') {
             selectable.onSelectionChange(Array.from(new Set([...totalPageIds]))); // Just select page as fallback
         } else if (mode === 'number') {
             const limitedIds = totalPageIds.slice(0, peopleCount);
             selectable.onSelectionChange(Array.from(new Set([...selectable.selectedIds, ...limitedIds])));
         }
     }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
         <button
           type="button"
           className={`flex items-center gap-2 rounded-md border px-2 py-1.5 transition-colors shadow-sm ${
             isChecked
               ? 'border-primary/40 bg-primary/5 text-foreground'
               : 'border-border bg-background text-muted-foreground hover:text-foreground hover:bg-muted/50'
           }`}
         >
           <span className={`flex h-4 w-4 items-center justify-center rounded border ${
             isChecked ? 'border-primary bg-primary text-primary-foreground' : 'border-gray-300 bg-background'
           }`}>
             {isChecked && <span className="block h-2 w-2 rounded-[2px] bg-current" />}
           </span>
           <ChevronDown className="w-3.5 h-3.5" />
         </button>
      </PopoverTrigger>

      <PopoverContent align="start" sideOffset={8} className="w-[340px] p-0 overflow-hidden bg-background border border-border rounded-2xl shadow-2xl z-[100]">
         <div className="p-0">
             <label className="flex flex-col gap-4 p-5 cursor-pointer border-b border-border/70">
                <div className="flex items-center gap-3">
                   <input 
                      type="radio" 
                      name="selectionMode" 
                      checked={mode === 'number'} 
                      onChange={() => setMode('number')}
                      className="w-4 h-4 accent-primary"
                   />
                   <span className="text-sm font-medium text-foreground">
                      Select number of people
                   </span>
                </div>
                <div className="pl-7 space-y-3">
                   <input 
                      type="number" 
                      min="1"
                      value={peopleCount} 
                      onChange={(e) => setPeopleCount(Number(e.target.value))}
                      className="w-full h-10 bg-background border border-border text-foreground text-sm font-semibold rounded-xl px-4 outline-none focus:border-primary transition-colors shadow-sm"
                   />
                   <div className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-3 cursor-pointer min-w-0">
                         <input 
                            type="checkbox" 
                            checked={maxPerCompanyCheck}
                            onChange={(e) => setMaxPerCompanyCheck(e.target.checked)}
                            className="w-4 h-4 accent-primary rounded"
                         />
                         <span className="text-sm text-foreground">Max people per company</span>
                      </label>
                      <input 
                         type="number" 
                         min="1"
                         value={maxPerCompany}
                         disabled={!maxPerCompanyCheck}
                         onChange={(e) => setMaxPerCompany(Number(e.target.value))}
                         className="w-24 h-10 bg-background border border-border text-foreground text-sm font-semibold rounded-xl px-3 outline-none focus:border-primary transition-colors shadow-sm disabled:opacity-50"
                      />
                   </div>
                </div>
             </label>

             <label className="flex items-center gap-3 px-5 py-4 cursor-pointer">
                <input 
                   type="radio" 
                   name="selectionMode" 
                   checked={mode === 'page'} 
                   onChange={() => setMode('page')}
                   className="w-4 h-4 accent-primary"
                />
                <span className="text-sm text-foreground">Select this page</span>
                <span className="text-sm text-muted-foreground">{totalPageIds.length}</span>
             </label>

             <label className="flex items-center gap-3 px-5 py-4 cursor-pointer">
                <input 
                   type="radio" 
                   name="selectionMode" 
                   checked={mode === 'all'} 
                   onChange={() => setMode('all')}
                   className="w-4 h-4 accent-primary"
                />
                <span className="text-sm text-foreground">Select all</span>
                <span className="text-sm text-muted-foreground">{totalRecords.toLocaleString()}</span>
             </label>
         </div>

         <div className="bg-muted/20 border-t border-border p-4 flex justify-end gap-3">
            <button 
               onClick={() => setIsOpen(false)}
               className="px-4 py-2 text-sm font-semibold text-foreground/80 hover:text-foreground transition-colors"
            >
               Cancel
            </button>
            <button 
               onClick={handleApply}
               className="px-5 py-2 text-sm font-bold text-white bg-primary hover:bg-primary/90 rounded-xl transition-all shadow-sm"
            >
               Apply
            </button>
         </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Reusable DataTable Component
 * Generates a Blueprint-styled table with server-side pagination, search, and optional filters.
 *
 * @param {Array} columns - Array of column objects: { header: 'Name', accessor: 'name' | function(row), sortable: true|false, render: function(row) }
 * @param {Array} data - Array of data objects
 * @param {Object} pagination - Pagination state: { page, limit, totalRecords, totalPages }
 * @param {Function} onPageChange - (newPage) => void
 * @param {Function} onLimitChange - (newLimit) => void
 * @param {Object} sort - Sort state: { sortBy, sortOrder }
 * @param {Function} onSort - (field) => void
 * @param {Object} search - Search config: { placeholder, value, onChange }
 * @param {Object} filter - Optional filter config: { value, onChange, options: [{label, value, icon}] }
 * @param {React.ReactNode} emptyState - Component/JSX to render when data is empty
 * @param {boolean} loading - Loading state
 */
export default function DataTable({
  columns,
  data,
  pagination,
  onPageChange,
  onLimitChange,
  sort,
  onSort,
  search,
  filter,
  headerExtra,
  topContent,
  bulkActions,
  selectable,
  emptyState,
  loading,
  onRowClick,
  variant = 'default'
}) {
  const [searchInput, setSearchInput] = useState(search?.value || '');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search && search.onChange && searchInput !== search.value) {
        search.onChange(searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, search]);
  
  // Sync prop changes back to local state (e.g. if cleared outside)
  useEffect(() => {
    if (search && search.value !== undefined && search.value !== searchInput) {
       setSearchInput(search.value);
    }
  }, [search?.value]);

  const { page = 1, limit = 10, totalRecords = 0, totalPages = 1 } = pagination || {};
  const { sortBy } = sort || {};

  const isWorkspace = variant === 'workspace';

  const containerClass = isWorkspace 
    ? "flex flex-col h-full min-h-0 bg-background relative" 
    : "space-y-4";

  const controlsClass = isWorkspace
    ? "px-4 py-3 shrink-0"
    : "px-6";

  const tableWrapperClass = isWorkspace
    ? "flex-1 flex flex-col min-h-0 bg-background relative overflow-hidden"
    : "bg-card rounded-[2rem] border border-border shadow-sm overflow-hidden transition-all duration-300";

  const tableScrollAreaClass = isWorkspace
    ? "flex-1 min-h-0 overflow-auto custom-scrollbar"
    : "overflow-x-auto";

  const theadClass = isWorkspace
    ? "bg-muted/30 sticky top-0 z-10 shadow-sm backdrop-blur-sm"
    : "bg-muted/30";

  const thClass = isWorkspace
    ? "px-4 py-3 text-left"
    : "px-6 py-4 text-left";

  const tdClass = isWorkspace
    ? "px-4 py-2"
    : "px-6 py-5";

  const rowClass = isWorkspace
    ? "hover:bg-muted/20 transition-colors group text-sm"
    : "hover:bg-muted/20 transition-colors group";

  const selectionLabel = selectable?.isAllSelected
    ? `${pagination?.totalRecords || 'All'} Selected (Global)`
    : `${selectable?.selectedIds?.length || 0} Selected`;

  return (
    <div className={containerClass}>
      {/* Table Controls (Search & Filter) */}
      {(search || filter) && (
        <div className={controlsClass}>
          <div className="flex flex-col md:flex-row gap-4 items-center bg-card p-4 rounded-[1.5rem] border border-border shadow-sm">
            {search && (
              <div className="relative flex-1 w-full max-w-md">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <input
                  type="text"
                  placeholder={search.placeholder || "Search..."}
                  className="w-full bg-muted/30 border-2 border-border/50 text-foreground text-sm font-semibold rounded-2xl pl-11 pr-4 py-3 focus:border-primary transition-all shadow-sm h-12"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            )}
            {filter && filter.options && (
              <div className="flex gap-3 w-full md:w-auto overflow-x-auto pb-1 md:pb-0 hide-scrollbar">
                <Select value={filter.value} onValueChange={filter.onChange}>
                  <SelectTrigger className="w-[140px] bg-muted/30 border-2 border-border/50 text-foreground font-semibold rounded-2xl h-12 shadow-sm">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-xl shadow-xl">
                    {filter.options.map(opt => (
                      <SelectItem key={opt.value} value={opt.value} className="font-bold cursor-pointer py-2 hover:bg-muted">
                        <div className="flex items-center">
                          {opt.icon && <span className="mr-2">{opt.icon}</span>}
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bulk Actions & Selection Bar */}
      {bulkActions && bulkActions.length > 0 && (selectable?.selectedIds?.length > 0 || selectable?.isAllSelected) && (
        <div className={isWorkspace ? "pointer-events-none absolute left-1/2 top-3 z-20 w-[calc(100%-2rem)] max-w-[1080px] -translate-x-1/2 px-2" : controlsClass}>
          <div className={`animate-in slide-in-from-top-2 duration-300 shadow-sm ${
            isWorkspace
              ? "pointer-events-auto flex items-center gap-1.5 rounded-2xl border border-border/70 bg-background/95 px-3 py-2 backdrop-blur-xl"
              : "flex items-center gap-3 bg-primary/5 p-3 rounded-2xl border border-primary/20 shadow-primary/5"
          }`}>
            <div className={`shrink-0 ${
              isWorkspace
                ? "px-3 py-2 rounded-xl bg-muted text-foreground text-[11px] font-bold"
                : "px-3 py-1 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-full shadow-sm"
            }`}>
              {selectionLabel}
            </div>
            <div className={`flex items-center gap-2 overflow-x-auto hide-scrollbar ${isWorkspace ? "flex-1" : "pb-1"}`}>
              {bulkActions.map((action, idx) => (
                <Button 
                  key={idx} 
                  onClick={action.onClick}
                  variant={action.variant === 'primary' ? 'default' : 'outline'}
                  className={`gap-2 whitespace-nowrap transition-all active:scale-95 ${
                    isWorkspace
                      ? action.variant === 'primary'
                        ? 'h-10 rounded-xl bg-primary hover:bg-primary/90 px-4 text-[11px] font-bold text-primary-foreground shadow-sm'
                        : 'h-10 rounded-xl border-transparent bg-transparent px-3 text-[11px] font-medium text-foreground hover:bg-muted'
                      : action.variant === 'primary'
                        ? 'h-9 px-4 text-xs font-black uppercase tracking-widest rounded-xl bg-primary hover:bg-primary/90 shadow-md shadow-primary/10'
                        : 'h-9 px-4 text-xs font-black uppercase tracking-widest rounded-xl border-primary/20 hover:bg-primary/5'
                  }`}
                >
                  {action.icon && <action.icon className="w-4 h-4" />}
                  {action.label}
                </Button>
              ))}
            </div>
            <Button 
              variant="ghost" 
              onClick={() => selectable.onSelectionChange([])}
              className={`ml-auto shrink-0 p-0 rounded-xl hover:bg-destructive/10 hover:text-destructive ${
                isWorkspace ? "h-10 w-10" : "h-9 w-9"
              }`}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {headerExtra && (
        <div className={`animate-in fade-in duration-500 mt-2 ${controlsClass}`}>
          {headerExtra}
        </div>
      )}

      {/* Loading & Empty States */}
      {loading && data.length === 0 ? (
        <div className="py-20 flex justify-center flex-1">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : data.length === 0 ? (
        <div className="flex-1 flex flex-col justify-center">
          {emptyState || <EmptyState />}
        </div>
      ) : (
        <div className={tableWrapperClass}>
          {/* Top Content (e.g. Dashboards) */}
          {topContent && (
             <div className="shrink-0">
               {topContent}
             </div>
          )}

          {/* Internal Table */}
          <div className={tableScrollAreaClass}>
            <div className="min-w-full">
            <table className="min-w-full divide-y divide-border">
              <thead className={theadClass}>
                <tr>
                  {selectable && (
                    <th className={`${thClass} w-12`}>
                      {selectable.advanced ? (
                         <AdvancedSelectHeader selectable={selectable} pagination={pagination} data={data} />
                      ) : (
                         <input 
                           type="checkbox" 
                           className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4 cursor-pointer"
                           checked={data.length > 0 && data.every(row => {
                             const rid = selectable.rowId ? selectable.rowId(row) : (row.assignmentId || row._id);
                             return selectable.selectedIds.includes(rid);
                           })}
                           onChange={(e) => {
                             if (e.target.checked) {
                               const allIds = data.map(row => selectable.rowId ? selectable.rowId(row) : (row.assignmentId || row._id));
                               selectable.onSelectionChange(Array.from(new Set([...selectable.selectedIds, ...allIds])));
                             } else {
                               const pageIds = data.map(row => selectable.rowId ? selectable.rowId(row) : (row.assignmentId || row._id));
                               selectable.onSelectionChange(selectable.selectedIds.filter(id => !pageIds.includes(id)));
                             }
                           }}
                         />
                      )}
                    </th>
                  )}
                  {columns.map((col, idx) => (
                    <th key={idx} scope="col" className={`${thClass} ${col.headerClassName || ''}`} style={getColumnStyle(col)}>
                      {col.sortable && onSort ? (
                        <button 
                          onClick={() => onSort(col.accessor)} 
                          className="group flex items-center text-[11px] font-black text-muted-foreground uppercase tracking-widest hover:text-foreground transition-colors w-full"
                          style={{ justifyContent: col.align === 'right' ? 'flex-end' : 'flex-start' }}
                        >
                          {col.header}
                          <ArrowUpDown className={`w-3.5 h-3.5 ml-2 transition-opacity ${sortBy === col.accessor ? 'opacity-100 text-primary' : 'opacity-0 group-hover:opacity-50'}`} />
                        </button>
                      ) : (
                        <span className="text-[11px] font-black text-muted-foreground uppercase tracking-widest block w-full" style={{ textAlign: col.align || 'left' }}>
                           {col.header}
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-background divide-y divide-border/50">
                {data.map((row, rowIndex) => {
                  const id = selectable?.rowId ? selectable.rowId(row) : (row.assignmentId || row._id);
                  const isSelected = selectable?.selectedIds?.includes(id);

                  return (
                    <tr 
                      key={id || rowIndex} 
                      className={`${rowClass} ${isSelected ? 'bg-primary/[0.02]' : ''} ${onRowClick ? 'cursor-pointer' : ''}`}
                      onClick={() => {
                        if (onRowClick) onRowClick(row);
                      }}
                    >
                      {selectable && (
                        <td className={tdClass} onClick={(e) => e.stopPropagation()}>
                          <input 
                            type="checkbox" 
                            className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4 cursor-pointer"
                            checked={isSelected || false}
                            onChange={() => {
                              if (isSelected) {
                                selectable.onSelectionChange(selectable.selectedIds.filter(i => i !== id));
                              } else {
                                selectable.onSelectionChange([...selectable.selectedIds, id]);
                              }
                            }}
                          />
                        </td>
                      )}
                      {columns.map((col, colIndex) => (
                        <td
                          key={colIndex}
                          className={`${tdClass} ${col.align === 'right' ? 'text-right' : ''} ${col.cellClassName || ''}`}
                          style={getColumnStyle(col)}
                        >
                          {col.render ? col.render(row) : (typeof col.accessor === 'function' ? col.accessor(row) : row[col.accessor])}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>

          {/* Pagination Footer */}
          {pagination && (
            <div className={isWorkspace ? "px-4 py-2 border-t border-border bg-background flex flex-col md:flex-row gap-4 items-center justify-between shrink-0" : "px-6 py-4 border-t border-border bg-muted/10 flex flex-col md:flex-row gap-4 items-center justify-between"}>
              <div className="flex items-center gap-4 w-full md:w-auto">
                <div className={`font-medium text-muted-foreground whitespace-nowrap ${isWorkspace ? 'text-xs' : 'text-sm'}`}>
                  Showing <span className="font-bold text-foreground">{(page - 1) * limit + 1}</span> to <span className="font-bold text-foreground">{Math.min(page * limit, totalRecords)}</span> of <span className="font-bold text-foreground">{totalRecords}</span> records
                </div>
                {onLimitChange && (
                  <Select value={limit.toString()} onValueChange={(val) => onLimitChange(Number(val))}>
                    <SelectTrigger className={`w-[80px] bg-background border border-border font-bold rounded-xl shadow-sm ${isWorkspace ? 'h-8 text-[11px]' : 'h-9 text-xs'}`}>
                      <SelectValue placeholder="25" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-xl shadow-xl min-w-[80px]">
                      <SelectItem value="10" className="font-bold text-xs cursor-pointer py-1.5 hover:bg-muted">10</SelectItem>
                      <SelectItem value="25" className="font-bold text-xs cursor-pointer py-1.5 hover:bg-muted">25</SelectItem>
                      <SelectItem value="50" className="font-bold text-xs cursor-pointer py-1.5 hover:bg-muted">50</SelectItem>
                      <SelectItem value="100" className="font-bold text-xs cursor-pointer py-1.5 hover:bg-muted">100</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="flex gap-2 w-full md:w-auto justify-end">
                <button
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className={`bg-background border border-border text-foreground rounded-xl shadow-sm font-bold hover:bg-muted transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${isWorkspace ? 'w-8 h-8' : 'p-2 text-sm'}`}
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages || totalPages === 0}
                  className={`bg-background border border-border text-foreground rounded-xl shadow-sm font-bold hover:bg-muted transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${isWorkspace ? 'w-8 h-8' : 'p-2 text-sm'}`}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
